import React from 'react';

interface User {
  id: string;
  firstName: string;
  lastName: string;
  profilePicture?: string;
}

interface ProfileAvatarProps {
  user: User;
  size?: number;
  className?: string;
  onClick?: () => void;
  cacheBuster?: number;
}

const ProfileAvatar: React.FC<ProfileAvatarProps> = ({ 
  user, 
  size = 40, 
  className = '', 
  onClick,
  cacheBuster = 0
}) => {
  const getInitials = (firstName: string, lastName: string): string => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const generateColor = (name: string): string => {
    // Generate a consistent color based on the user's name
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    const colors = [
      '#4CAF50', '#2196F3', '#FF9800', '#9C27B0', '#F44336',
      '#009688', '#795548', '#607D8B', '#E91E63', '#3F51B5',
      '#FF5722', '#8BC34A', '#00BCD4', '#FFC107', '#673AB7'
    ];
    
    return colors[Math.abs(hash) % colors.length];
  };

  const avatarStyle: React.CSSProperties = {
    width: size,
    height: size,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: size * 0.4,
    fontWeight: 600,
    color: 'white',
    cursor: onClick ? 'pointer' : 'default',
    transition: 'all 0.2s ease',
    border: '2px solid rgba(255, 255, 255, 0.2)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  };

  if (user.profilePicture) {
    const imageUrl = `/uploads/profile-pictures/${user.profilePicture}?v=${cacheBuster}`;
    
    return (
      <div
        className={`profile-avatar ${className}`}
        style={{
          ...avatarStyle,
          backgroundImage: `url(${imageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        onClick={onClick}
        title={`${user.firstName} ${user.lastName}`}
      />
    );
  }

  return (
    <div
      className={`profile-avatar ${className}`}
      style={{
        ...avatarStyle,
        backgroundColor: generateColor(`${user.firstName}${user.lastName}`),
      }}
      onClick={onClick}
      title={`${user.firstName} ${user.lastName}`}
    >
      {getInitials(user.firstName, user.lastName)}
    </div>
  );
};

export default ProfileAvatar; 